/* eslint-disable react/prop-types */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { CookieItemKeyEnum } from '../enums/CookieItemKeyEnum';
import { FileTypeEnum } from '../enums/FileTypeEnum';
import { StorageItemKeyEnum } from '../enums/StorageItemKeyEnum';
import { useAuth } from '../hooks/useAuth';
import type InfoViewer from '../interfaces/InfoViewer';
import { getCookie } from '../utils/cookie';
import { getStorageItem } from '../utils/storage';
import { getOfficeFileBaseUrl, getOfficeFileFavicon } from '../utils/utils';

interface OfficeProps {
  type: FileTypeEnum;
}

export default function Office({ type }: OfficeProps) {
  const [accessToken, setAccessToken] = useState('');
  const [accessTokenExpiry, setAccessTokenExpiry] = useState(0);
  const [wopiSource, setWopiSource] = useState('');
  const [_, setFileInfo] = useState<InfoViewer>();

  const navigate = useNavigate();
  const { deviceInfo, token } = useAuth();
  const { fileGuid } = useParams();

  useEffect(() => {
    let baseUrl = '';
    let favIcon = '';

    switch (type) {
      case FileTypeEnum.Word:
        baseUrl = getOfficeFileBaseUrl(FileTypeEnum.Word);
        favIcon = getOfficeFileFavicon(FileTypeEnum.Word);
        break;
      case FileTypeEnum.Excel:
        baseUrl = getOfficeFileBaseUrl(FileTypeEnum.Excel);
        favIcon = getOfficeFileFavicon(FileTypeEnum.Excel);
        break;
      case FileTypeEnum.Powerpoint:
        baseUrl = getOfficeFileBaseUrl(FileTypeEnum.Powerpoint);
        favIcon = getOfficeFileFavicon(FileTypeEnum.Powerpoint);
        break;
      case FileTypeEnum.Wopitest:
        baseUrl = getOfficeFileBaseUrl(FileTypeEnum.Wopitest);
        favIcon = getOfficeFileFavicon(FileTypeEnum.Wopitest);
    }

    const wopiSrc = `${import.meta.env.VITE_APP_OFFICE_WOPISOURCE_URL}/api/wopi/files/${fileGuid}`;

    const url = `${baseUrl}wopisrc=${encodeURIComponent(wopiSrc)}&IsLicensedUser=1&ui=${deviceInfo?.culture}&rs=${deviceInfo?.culture}`;

    setWopiSource(url);

    if (!token) {
      navigate('/login');
      return;
    }

    (async () => {
      const fileInfoViewerFromStorage = await getStorageItem(
        'sessionStorage',
        StorageItemKeyEnum.FileOpened,
      );
      setFileInfo(fileInfoViewerFromStorage);
    })();

    setAccessToken(token);

    const timeStamp = new Date();
    timeStamp.setDate(timeStamp.getDate() + 1);
    setAccessTokenExpiry(timeStamp.getTime());

    // Insert the favicon
    const appIcon: HTMLLinkElement | null = document.querySelector("link[rel~='icon']");
    if (appIcon) {
      appIcon.href = favIcon;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const cookieOffice = getCookie(CookieItemKeyEnum.officeFile);

    document.title = cookieOffice || 'Office';

    const office_frame = document.getElementById('office_frame');
    if (office_frame) return;

    if (accessTokenExpiry === 0) return;

    buildOfficeFrame();
  }, [accessTokenExpiry]);

  /**
   * Builds and appends an iframe element intended for displaying Office content.
   * The function targets a container with the ID "frameholder" and appends
   * a configured iframe element with specific attributes and styling.
   * It also triggers a form submission on a form element with the ID "office_form".
   *
   * Functionality:
   * - Creates an iframe with specific attributes:
   *   - `name` and `id` set as "office_frame".
   *   - `title` added for accessibility purposes.
   *   - `allowfullscreen` enabled for full-screen mode during PowerPoint slideshow view.
   *   - `sandbox` configured to allow specific interactions (e.g., popups, scripts, navigation).
   *   - Adds pre-defined CSS classes for styling purposes.
   * - Appends the iframe to a DOM element with the ID "frameholder".
   * - Automatically submits a form element identified by the ID "office_form".
   *
   * Accessibility:
   * - The `title` attribute is added for screen reader compatibility.
   *
   * Error Handling:
   * - Ensures that the "frameholder" container and "office_form" exist in the DOM
   *   before attempting to append the iframe or submit the form.
   */
  const buildOfficeFrame = () => {
    const frameholder = document.getElementById('frameholder');
    const office_frame = document.createElement('iframe');
    office_frame.name = 'office_frame';
    office_frame.id = 'office_frame';

    // The title should be set for accessibility
    office_frame.title = 'Office Frame';

    // This attribute allows true fullscreen mode in slideshow view
    // when using PowerPoint's 'view' action.
    office_frame.setAttribute('allowfullscreen', 'true');

    // The sandbox attribute is needed to allow automatic redirection to the O365 sign-in page in the business user flow
    office_frame.setAttribute(
      'sandbox',
      'allow-scripts allow-same-origin allow-forms allow-popups allow-top-navigation allow-popups-to-escape-sandbox',
    );
    // Set class for styling
    office_frame.className = 'w-full h-full absolute inset-0 border-none block';
    frameholder?.appendChild(office_frame);

    // Effettua un type assertion per informare TypeScript che si tratta di un HTMLFormElement
    const officeForm = document.getElementById('office_form') as HTMLFormElement;

    // Verifica che l'elemento esista prima di chiamare .submit()
    officeForm?.submit();
  };

  return (
    <main>
      <div className="overflow-hidden p-0">
        <form
          id="office_form"
          name="office_form"
          target="office_frame"
          action={wopiSource}
          method="post"
        >
          <input name="access_token" value={accessToken} type="hidden" />
          <input name="access_token_ttl" value={accessTokenExpiry} type="hidden" />
        </form>
        <span id="frameholder"></span>
      </div>
    </main>
  );
}
